import { CircularProgress, FormHelperText, Link } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { MdDelete } from 'react-icons/md';
import { useToastValue } from '../../../../contexts/toastMessage';
import { sendFile } from '../../../../services/form-flow';
import { validateAndReturnResponse, handleApiError } from '../../../../utils/apiValidation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#264259',
      },
      display: 'block',
    },
  },
  input: {
    color: theme.palette.primary.main,
    border: '1px solid #264259',
    borderRadius: '4px',
    fontSize: '16px',
    padding: '2px',
    width: '100%',
  },
  inputError: {
    color: theme.palette.primary.main,
    border: '2px solid #f44336',
    borderRadius: '4px',
    fontSize: '16px',
    padding: '2px',
    width: '100%',
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  boxFile: {
    display: 'flex',
    border: '1px solid #e0e0e0',
    justifyContent: 'space-between',
    padding: '10px',
    borderRadius: '10px',
  },
  button: {
    border: 'none',
    background: 'transparent',
    color: '#264259',
  },
  boxButton: {
    borderLeft: '1px solid #e0e0e0',
    paddingLeft: '10px',
  },
}));

type Props = {
  required?: boolean;
  id: string;
  label: string;
  value:
  | ''
  | {
    url: string;
    nome: string;
    id: string;
  };
  error?: boolean;
  onChange: (
    value:
      | ''
      | {
        url: string;
        nome: string;
        id: string;
      },
  ) => void;
  helperText?: string;
  disabled?: boolean;
  tipos_arquivos?: string[];
  tamanho_maximo_permitido?: number;
};

export default function Arquivo(props: Props): JSX.Element {
  const classes = useStyles();
  const { setToast } = useToastValue();
  const [loading, setLoading] = React.useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [file, setFile] = React.useState<File | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  React.useEffect(() => {
    if (!props.value || (typeof props.value === 'string' && props.value === '')) {
      setFile(null);
    }
  }, [props.value]);

  const getAcceptAttribute = (): string => {
    if (!props.tipos_arquivos || props.tipos_arquivos.length === 0) {
      return '';
    }
    return props.tipos_arquivos.map(ext => `.${ext.toLowerCase()}`).join(',');
  };

  const validateFileExtension = (fileName: string): boolean => {
    if (!props.tipos_arquivos || props.tipos_arquivos.length === 0) {
      return true;
    }
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return props.tipos_arquivos.some(ext => ext.toLowerCase() === extension);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const afile = event.target.files?.[0];

      if (!afile) {
        setLoading(false);
        return;
      }

      if (!validateFileExtension(afile.name)) {
        const tiposPermitidos = props.tipos_arquivos?.map(ext => `.${ext.toLowerCase()}`).join(', ') || '';
        const errorMsg = `Extensão não permitida. Tipos permitidos: ${tiposPermitidos}`;
        setErrorMessage(errorMsg);
        setToast({
          visible: true,
          type: 'error',
          message: errorMsg,
        });
        event.target.value = '';
        setLoading(false);
        return;
      }

      if (props.tamanho_maximo_permitido && props.tamanho_maximo_permitido > 0) {
        const tamanhoMaximoBytes = props.tamanho_maximo_permitido;
        const tamanhoArquivoBytes = afile.size;
        if (tamanhoArquivoBytes > tamanhoMaximoBytes) {
          const tamanhoMaximo = formatFileSize(tamanhoMaximoBytes);
          const tamanhoArquivo = formatFileSize(tamanhoArquivoBytes);
          const errorMsg = `Arquivo muito grande (${tamanhoArquivo}). Tamanho máximo permitido: ${tamanhoMaximo}`;
          setErrorMessage(errorMsg);
          setToast({
            visible: true,
            type: 'error',
            message: errorMsg,
          });
          event.target.value = '';
          setLoading(false);
          return;
        }
      }

      setFile(afile);
      const response = await sendFile(afile, false);
      validateAndReturnResponse(response);

      const dataSend = {
        url: response.data.arquivo,
        nome: afile.name,
        id: response.data.id,
      };
      props.onChange(dataSend);
      setErrorMessage('');
    } catch (error) {
      const errorMessage = handleApiError(error as any, true);

      setErrorMessage(errorMessage);

      setToast({
        visible: true,
        type: 'error',
        message: `Erro ao enviar arquivo: ${errorMessage}`,
      });

      setFile(null);
      event.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setLoading(true);
    setFile(null);
    setLoading(false);
    props.onChange('');
  };

  return (
    <>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <label htmlFor={props.id} className={classes.label}>
            {props.label.replace(/(<([^>]+)>)/gi, '')}
          </label>
          {!errorMessage && props.value && typeof props.value === 'object' && props.value.url && props.value.nome ? (
            <div className={classes.boxFile}>
              <div>
                <Link href={props.value.url} target='_blank'>
                  {props.value.nome}
                </Link>
              </div>
              <div className={classes.boxButton}>
                <button className={classes.button} onClick={handleRemove}>
                  <MdDelete size={16} />
                </button>
              </div>
            </div>
          ) : (
            <>
              {props.disabled && props.value && typeof props.value === 'object' && props.value.url && props.value.nome ? (
                <div className={classes.boxFile}>
                  <Link href={props.value.url} target='_blank'>
                    {props.value.nome}
                  </Link>
                </div>
              ) : (
                <>
                  <input
                    type='file'
                    id={props.id}
                    className={errorMessage ? classes.inputError : classes.input}
                    name={props.id}
                    onChange={handleChange}
                    disabled={props.disabled}
                    required={props.required}
                    accept={getAcceptAttribute()}
                  />
                  {errorMessage && (
                    <FormHelperText error={true} style={{ marginTop: 8 }}>
                      {errorMessage}
                    </FormHelperText>
                  )}
                  {!errorMessage && props.helperText && (
                    <FormHelperText error={props.error}>
                      {props.helperText}
                    </FormHelperText>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}